// ConsultantMemoryMenu — the header "⋯" control for consultant personalization
// (Moat Plan A1/A2 user controls). One component, used by the shared
// ConsultantChat header, so it appears on both the desktop drawer and the
// Clara mobile screen.
//
// Lets the user: see whether the consultant is personalized, read what it
// currently remembers about them, turn personalization off (generic answers),
// and clear the remembered context. Point-of-use control — NOT buried in
// Settings.

import { useState } from "react";
import { MoreHorizontal, Check, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useConsultantMemory } from "@/hooks/useConsultantMemory";

export function ConsultantMemoryMenu({
  isClara = false,
  claraColor,
}: {
  isClara?: boolean;
  claraColor?: string;
}) {
  const { enabled, summary, topics, loading, setEnabled, clearMemory, isSaving } =
    useConsultantMemory();
  const [open, setOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setConfirmClear(false);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Personalization & memory"
          className="h-8 w-8"
          style={isClara && claraColor ? { color: claraColor } : undefined}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          The consultant {enabled ? "is personalized to your operation" : "is in generic mode"}
        </DropdownMenuLabel>

        {/* Toggle */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setEnabled(!enabled);
          }}
          disabled={isSaving}
          className="flex items-center justify-between gap-2"
        >
          <span>Personalize answers to me</span>
          {enabled && <Check className="h-4 w-4 shrink-0" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* What it remembers */}
        <DropdownMenuLabel className="text-xs font-semibold">What it remembers</DropdownMenuLabel>
        <div className="px-2 pb-2 pt-0.5 max-h-40 overflow-y-auto">
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : summary ? (
            <>
              <p className="text-xs text-muted-foreground leading-relaxed">{summary}</p>
              {topics.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {topics.map((t) => (
                    <span
                      key={t}
                      className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              Nothing yet — it learns the topics you ask about as you chat.
            </p>
          )}
        </div>

        {summary && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                if (!confirmClear) {
                  setConfirmClear(true);
                  return;
                }
                clearMemory();
                setConfirmClear(false);
                setOpen(false);
              }}
              disabled={isSaving}
              className={cn(
                "gap-2",
                confirmClear ? "text-destructive font-medium" : "text-muted-foreground",
              )}
            >
              <Trash2 className="h-4 w-4 shrink-0" />
              <span>{confirmClear ? "Tap again to clear" : "Clear what it remembers"}</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
